import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { shopItems as baseShopItems, shopFaq, groupItemsByCollection } from './shopItems';
import { mergeCatalog, visibleCatalog } from './catalog';

const CatalogContext = createContext(null);

const catalogFrom = (products, overrides, reserved, hidden) =>
    mergeCatalog(baseShopItems, products, overrides, reserved, hidden);

export const CatalogProvider = ({ children }) => {
    const [overrides, setOverrides] = useState({});
    const [customProducts, setCustomProducts] = useState([]);
    const [reserved, setReserved] = useState({});
    const [hiddenIds, setHiddenIds] = useState([]);
    const [checking, setChecking] = useState(true);
    const [error, setError] = useState('');
    const stateRef = useRef({ customProducts, overrides, reserved, hiddenIds });
    stateRef.current = { customProducts, overrides, reserved, hiddenIds };

    const refresh = useCallback(async () => {
        try {
            const response = await fetch('/api/catalog');
            if (!response.ok) {
                setError('The latest availability could not be loaded. Please try again before ordering.');
                const current = stateRef.current;
                return visibleCatalog(catalogFrom(
                    current.customProducts,
                    current.overrides,
                    current.reserved,
                    current.hiddenIds
                ));
            }
            const data = await response.json();
            setError('');
            const nextOverrides = data?.overrides && typeof data.overrides === 'object' ? data.overrides : {};
            const nextProducts = Array.isArray(data?.products) ? data.products : [];
            const nextReserved = data?.reserved && typeof data.reserved === 'object' ? data.reserved : {};
            const nextHidden = Array.isArray(data?.hidden) ? data.hidden : [];
            setOverrides(nextOverrides);
            setCustomProducts(nextProducts);
            setReserved(nextReserved);
            setHiddenIds(nextHidden);
            return visibleCatalog(catalogFrom(nextProducts, nextOverrides, nextReserved, nextHidden));
        } catch (error) {
            setError('The latest availability could not be loaded. Please try again before ordering.');
            const current = stateRef.current;
            return visibleCatalog(catalogFrom(
                current.customProducts,
                current.overrides,
                current.reserved,
                current.hiddenIds
            ));
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const allItems = useMemo(
        () => catalogFrom(customProducts, overrides, reserved, hiddenIds),
        [customProducts, overrides, reserved, hiddenIds]
    );
    const shopItems = useMemo(() => visibleCatalog(allItems), [allItems]);
    const shopCategories = useMemo(() => {
        const isSticker = (item) => String(item.format || '').toLowerCase() === 'sticker';
        const names = [];
        shopItems.forEach((item) => {
            if (!item.category || isSticker(item)) {
                return;
            }
            if (!names.includes(item.category)) {
                names.push(item.category);
            }
        });
        return [
            'All',
            ...(shopItems.some(isSticker) ? ['Sticker'] : []),
            ...names,
        ];
    }, [shopItems]);
    const hachiokiItems = useMemo(
        () => shopItems.filter((item) => item.collection === 'hachioki'),
        [shopItems]
    );

    const getShopItem = (id) => shopItems.find((item) => item.id === id);

    return (
        <CatalogContext.Provider
            value={{
                shopItems,
                allItems,
                shopCategories,
                shopFaq,
                hachiokiItems,
                getShopItem,
                groupItemsByCollection,
                overrides,
                reserved,
                refresh,
                checking,
                error,
            }}
        >
            {children}
        </CatalogContext.Provider>
    );
};

export const useCatalog = () => {
    const context = useContext(CatalogContext);
    if (!context) {
        throw new Error('useCatalog must be used within CatalogProvider');
    }
    return context;
};
